import { mapPreviewOpacity } from "../shared/mapPresentation";
import { useId } from "preact/hooks";
import { mapPolygonBoundary, mapPolygonCornerIndices } from "./venueMapPolygon";
import { VenueMapBoothLabel } from "./VenueMapBoothLabel";
import { venueMapBoothLabelRotation } from "./venueMapBoothLabelLayout";
import type { VenueMapLogos } from "./useVenueMapLogos";
import type { MapDocument, MapStand } from "../shared/types";
import { orderMapFaces } from "./venueMapFaceOrder";
import {
  mapStandColors,
  mapStandRoofTint,
  mapStandBorderColor,
} from "./venueMapAppearance";
import {
  VENUE_MAP_ARCHITECTURE_COLOR,
  VENUE_MAP_ARCHITECTURE_HEIGHT,
  venueMapArchitectureRings,
} from "./venueMapArchitecture";
import {
  VENUE_MAP_ARTWORK_HEIGHT,
  VENUE_MAP_ARTWORK_LABEL_HEIGHT,
  venueMapArtworkRings,
} from "./venueMapArtwork";
import { VenueMapRoute, type VenueMapRouteData } from "./VenueMapRoute";
import { VenueMapEntranceDots, VenueMapEntrances } from "./VenueMapEntrances";
import {
  venueMapLighting,
  venueMapWallLight,
  VENUE_MAP_RIM_TINT,
} from "./venueMapLighting";
import {
  mapStandHeight,
  projectMapBasis,
  projectMapDepth,
  projectMapPoint,
  type VenueMapCamera,
} from "./venueMapProjection";

type Point = { x: number; y: number; z?: number };
type Stand = MapStand;
type Face = {
  key: string;
  points: Point[];
  fill: string;
  stand: Stand;
  roof: boolean;
  corners?: number[];
};

function shade(hex: string, amount: number) {
  const channel = (offset: number) =>
    Math.round(parseInt(hex.slice(offset, offset + 2), 16) * amount)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

function tint(hex: string, amount: number) {
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16);
    return Math.round(value + (255 - value) * amount)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

function signedArea(points: Point[]) {
  return points.reduce((area, point, index) => {
    const next = points[(index + 1) % points.length];
    return area + point.x * next.y - next.x * point.y;
  }, 0);
}

export interface VenueMapSceneProps {
  document: MapDocument;
  camera: VenueMapCamera;
  viewportBounds: { x: number; y: number; width: number; height: number };
  description: string;
  route?: VenueMapRouteData;
  selectedStandId?: string;
  logos?: VenueMapLogos;
}

export function VenueMapScene({
  document,
  camera,
  viewportBounds,
  description,
  route,
  selectedStandId,
  logos,
}: VenueMapSceneProps) {
  const selectionMaskId = useId();
  const { geometry } = document;
  const standLabelPaths = document.labels.stands;
  const scale = camera.imageWidth / geometry.width;
  const viewportWidth = Math.max(1, viewportBounds.width);
  const viewportHeight = Math.max(1, viewportBounds.height);
  const lighting = venueMapLighting(camera);
  const mapPoint = (point: Point) => ({
    x: point.x / geometry.width,
    y: point.y / geometry.height,
    z: point.z || 0,
  });
  const projected = (point: Point) => projectMapPoint(camera, mapPoint(point));
  const pointsAttribute = (points: Point[]) =>
    points
      .map((point) => {
        const position = projected(point);
        return `${position.x.toFixed(3)},${position.y.toFixed(3)}`;
      })
      .join(" ");
  const depth = (points: Point[]) =>
    points.reduce(
      (sum, point) => sum + projectMapDepth(camera, mapPoint(point)),
      0,
    ) / points.length;

  // Extrude every boundary edge, including the inset of the L-shaped stand.
  // Projected winding tests the face against the positional camera, so side
  // walls can become visible at either edge of the perspective view.
  const walls = (points: Point[], bottom: number, top: number) => {
    if (top <= bottom) return [];
    const winding = Math.sign(signedArea(points));
    return points.flatMap((point, index) => {
      const next = points[(index + 1) % points.length];
      const wallPoints = [
        { ...point, z: bottom },
        { ...next, z: bottom },
        { ...next, z: top },
        { ...point, z: top },
      ];
      if (winding * signedArea(wallPoints.map(projected)) <= 0.0001) {
        return [];
      }
      const dx = next.x - point.x;
      const dy = next.y - point.y;
      const normalX = winding * dy;
      const normalY = -winding * dx;
      return [
        {
          index,
          points: wallPoints,
          light: venueMapWallLight(
            { x: normalX, y: normalY },
            lighting.direction,
          ),
        },
      ];
    });
  };

  const faces: Face[] = geometry.stands.flatMap((stand) => {
    if (stand.hidden) return [];
    const height = mapStandHeight(stand);
    const color = mapStandColors(stand);
    return stand.polygons.flatMap((polygon, polygonIndex) => {
      const footprint = mapPolygonBoundary(polygon.map(([x, y]) => ({ x, y })));
      const corners = new Set(mapPolygonCornerIndices(footprint));
      const roof = footprint.map((point) => ({ ...point, z: height }));
      return [
        ...walls(footprint, 0, height).map((wall) => ({
          key: `${stand.id}-${polygonIndex}-wall-${wall.index}`,
          points: wall.points,
          corners: [0, 1].filter((index) =>
            corners.has((wall.index + index) % footprint.length),
          ),
          fill: shade(color.roof, wall.light),
          stand,
          roof: false,
        })),
        {
          key: `${stand.id}-${polygonIndex}-roof`,
          points: roof,
          fill: color.roof,
          stand,
          roof: true,
        },
      ];
    });
  });
  const orderedFaces = orderMapFaces(faces, (point) => ({
    ...projected(point),
    depth: depth([point]),
  }));

  // A shared corner belongs to the last visible wall painted there. Drawing it
  // once avoids a darker seam when the editor previews a hidden booth at 50%.
  const cornerKey = (face: Face, point: Point) =>
    `${face.stand.id}:${point.x}:${point.y}`;
  const cornerOwners = new Map<string, string>();
  for (const face of orderedFaces) {
    if (face.roof || !mapStandBorderColor(face.stand)) continue;
    for (const index of face.corners ?? [])
      cornerOwners.set(cornerKey(face, face.points[index]), face.key);
  }

  // Paint selection after its own walls, then mask only nearer *other* booths.
  // The painter ordering already establishes occlusion, so this adds a linear
  // scan for the selected roof rather than another face-intersection pass.
  const selectionFaces = orderedFaces.flatMap((face, index) => {
    if (!face.roof || face.stand.id !== selectedStandId) return [];
    const projectedRoof = face.points.map(projected);
    const left = Math.min(...projectedRoof.map((point) => point.x)) - 4;
    const right = Math.max(...projectedRoof.map((point) => point.x)) + 4;
    const top = Math.min(...projectedRoof.map((point) => point.y)) - 4;
    const bottom = Math.max(...projectedRoof.map((point) => point.y)) + 4;
    const occluders = orderedFaces.slice(index + 1).flatMap((other) => {
      if (other.stand.id === selectedStandId) return [];
      const points = other.points.map(projected);
      if (
        Math.max(...points.map((point) => point.x)) < left ||
        Math.min(...points.map((point) => point.x)) > right ||
        Math.max(...points.map((point) => point.y)) < top ||
        Math.min(...points.map((point) => point.y)) > bottom
      )
        return [];
      return [
        {
          key: other.key,
          points: pointsAttribute(other.points),
          opacity: mapPreviewOpacity(other.stand),
        },
      ];
    });
    return [{ face, occluders, maskId: `${selectionMaskId}-${index}` }];
  });
  const boothLabel = (face: Face) => {
    const label = face.stand.label;
    const labelAnchor = mapPoint({
      x: label.x,
      y: label.y,
      z: mapStandHeight(face.stand),
    });
    const labelPoint = projectMapPoint(camera, labelAnchor);
    const labelBasis = projectMapBasis(camera, labelAnchor);
    const lettering = (
      <g
        className="venue-map-stand-label"
        transform={`matrix(${labelBasis.a} ${labelBasis.b} ${labelBasis.c} ${labelBasis.d} ${labelPoint.x} ${labelPoint.y}) rotate(${venueMapBoothLabelRotation(face.stand)})`}
        fill={mapStandColors(face.stand).ink}
      >
        <VenueMapBoothLabel
          stand={face.stand}
          outline={standLabelPaths[face.stand.id]}
          logo={face.stand.logo ? logos?.get(face.stand.logo.id) : undefined}
        />
      </g>
    );
    if (face.stand.polygons.length === 1) return lettering;
    // A multipart booth shares one label layout. Each face paints only its own
    // piece so another component cannot redraw lettering over a nearer booth.
    const clipId = `${selectionMaskId}-label-${face.key}`;
    return (
      <g>
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <polygon points={pointsAttribute(face.points)} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>{lettering}</g>
      </g>
    );
  };

  const { floor } = geometry;
  const floorPoints = [
    { x: floor.x, y: floor.y },
    { x: floor.x + floor.width, y: floor.y },
    { x: floor.x + floor.width, y: floor.y + floor.height },
    { x: floor.x, y: floor.y + floor.height },
  ];
  const floorWalls = walls(floorPoints, -5, 0).sort(
    (a, b) => depth(a.points) - depth(b.points),
  );
  const architecturePath = geometry.architecture
    ? venueMapArchitectureRings(geometry.architecture)
        .map(
          (ring) =>
            `M${ring
              .map(([x, y]) => {
                const point = projected({
                  x,
                  y,
                  z: VENUE_MAP_ARCHITECTURE_HEIGHT,
                });
                return `${point.x.toFixed(3)},${point.y.toFixed(3)}`;
              })
              .join("L")}Z`,
        )
        .join(" ")
    : undefined;

  return (
    <svg
      className="venue-map-scene"
      width={viewportWidth}
      height={viewportHeight}
      viewBox={`${viewportBounds.x} ${viewportBounds.y} ${viewportWidth} ${viewportHeight}`}
      role="img"
      aria-label={description}
      data-rotation={camera.rotation}
      data-pitch={camera.pitch}
      style={{
        position: "absolute",
        left: viewportBounds.x,
        top: viewportBounds.y,
        overflow: "hidden",
      }}
    >
      <g aria-hidden="true" pointerEvents="none">
        {floorWalls.map((wall) => (
          <polygon
            key={wall.index}
            points={pointsAttribute(wall.points)}
            fill={shade("#d6d9d1", wall.light + 0.05)}
            stroke={shade("#d6d9d1", wall.light + 0.01)}
            strokeWidth={0.4 * scale}
            strokeLinejoin="round"
          />
        ))}
        <polygon
          className="venue-map-floor"
          points={pointsAttribute(floorPoints)}
          fill={geometry.floor.color ?? "#faf8f2"}
          stroke="#d1d7ce"
          strokeWidth={0.8 * scale}
          strokeLinejoin="round"
        />
        {geometry.artwork?.map((item, index) => (
          <path
            key={index}
            data-map-artwork={index}
            fill={item.color}
            opacity={item.opacity ?? 1}
            fillRule="nonzero"
            d={venueMapArtworkRings(item)
              .map((ring) => {
                const points = ring.map(([x, y]) => {
                  const point = projected({
                    x,
                    y,
                    z: VENUE_MAP_ARTWORK_HEIGHT,
                  });
                  return `${point.x.toFixed(3)},${point.y.toFixed(3)}`;
                });
                return `M${points.join("L")}Z`;
              })
              .join("")}
          />
        ))}
        {architecturePath && (
          <path
            className="venue-map-architecture"
            d={architecturePath}
            fill={VENUE_MAP_ARCHITECTURE_COLOR}
            fillRule="nonzero"
          />
        )}
        {geometry.artworkLabels?.map((label, index) => {
          const anchor = mapPoint({
            x: label.x,
            y: label.y,
            z: VENUE_MAP_ARTWORK_LABEL_HEIGHT,
          });
          const point = projectMapPoint(camera, anchor);
          const basis = projectMapBasis(camera, anchor);
          return (
            <g
              key={index}
              data-map-artwork-label={index}
              transform={`matrix(${basis.a} ${basis.b} ${basis.c} ${basis.d} ${point.x} ${point.y}) rotate(${label.rotation ?? 0})`}
            >
              <text
                x="0"
                y="0"
                fill={label.color}
                fontFamily="Inter, system-ui, sans-serif"
                fontSize={label.fontSize}
                fontWeight="600"
              >
                {label.text}
              </text>
            </g>
          );
        })}
        <VenueMapEntranceDots camera={camera} geometry={geometry} />
        {route && (
          <VenueMapRoute
            route={route}
            geometry={geometry}
            project={(point) =>
              // Centre the line between the visible roof and ground edges of
              // the raised booths; the correction follows the camera rotation.
              projectMapPoint(camera, {
                ...point,
                z: mapStandHeight("exhibitor") / 2,
              })
            }
          />
        )}
        {orderedFaces.map((face) => {
          const color = mapStandColors(face.stand);
          const borderColor = mapStandBorderColor(face.stand);
          const cornerPath =
            !face.roof && borderColor
              ? (face.corners ?? [])
                  .filter(
                    (index) =>
                      cornerOwners.get(cornerKey(face, face.points[index])) ===
                      face.key,
                  )
                  .map(
                    (index) =>
                      `M${pointsAttribute([face.points[index], face.points[3 - index]])}`,
                  )
                  .join("")
              : "";
          return (
            <g
              key={face.key}
              opacity={mapPreviewOpacity(face.stand)}
              data-map-opacity={mapPreviewOpacity(face.stand)}
            >
              <polygon
                className={
                  face.roof ? "venue-map-stand-top" : "venue-map-stand-side"
                }
                data-map-stand={face.stand.id}
                points={pointsAttribute(face.points)}
                fill={
                  face.roof
                    ? tint(color.roof, mapStandRoofTint(face.stand))
                    : face.fill
                }
                stroke={
                  face.roof
                    ? (borderColor ?? tint(face.fill, VENUE_MAP_RIM_TINT))
                    : face.fill
                }
                strokeWidth={0.65 * scale}
                strokeLinejoin="round"
              >
                {face.roof && (
                  <title>
                    {face.stand.id}
                    {face.stand.name && face.stand.name !== "."
                      ? ` · ${face.stand.name}`
                      : ""}
                  </title>
                )}
              </polygon>
              {cornerPath && (
                <path
                  className="venue-map-stand-corners"
                  data-map-stand={face.stand.id}
                  d={cornerPath}
                  fill="none"
                  stroke={borderColor}
                  strokeWidth={0.65 * scale}
                  strokeLinecap="round"
                />
              )}
              {face.roof &&
                face.stand.id !== selectedStandId &&
                boothLabel(face)}
            </g>
          );
        })}
        {selectionFaces.map(({ face, occluders, maskId }) => (
          <g
            key={face.key}
            opacity={mapPreviewOpacity(face.stand)}
            data-map-opacity={mapPreviewOpacity(face.stand)}
          >
            {occluders.length > 0 && (
              <defs>
                <mask
                  id={maskId}
                  maskUnits="userSpaceOnUse"
                  x={viewportBounds.x}
                  y={viewportBounds.y}
                  width={viewportWidth}
                  height={viewportHeight}
                >
                  <rect
                    x={viewportBounds.x}
                    y={viewportBounds.y}
                    width={viewportWidth}
                    height={viewportHeight}
                    fill="#fff"
                  />
                  {occluders.map((other) => (
                    <polygon
                      key={other.key}
                      points={other.points}
                      opacity={other.opacity}
                      fill="#000"
                    />
                  ))}
                </mask>
              </defs>
            )}
            <g mask={occluders.length ? `url(#${maskId})` : undefined}>
              <g
                className="venue-map-stand-selection"
                data-selected-stand={face.stand.id}
                fill="none"
                strokeLinejoin="round"
              >
                <polygon
                  points={pointsAttribute(face.points)}
                  stroke="#fff"
                  strokeWidth="6.5"
                />
                <polygon
                  points={pointsAttribute(face.points)}
                  stroke="#c6006c"
                  strokeWidth="3"
                />
              </g>
              {boothLabel(face)}
            </g>
          </g>
        ))}
        <VenueMapEntrances camera={camera} geometry={geometry} />
      </g>
    </svg>
  );
}
